import { location } from 'cdui-js';
import i18n from '../../../i18n';
import { userState } from '../../../stores/user';
import {
  appendDepositAppQuery,
  getDepositEntry,
  type ExchangePrefetchPayload,
} from '../deposit/h5-deposit/appBridge';
import { currencySpriteName } from '../../../assets/currencyFlags';

export type ExchangeAccount = {
  accountNum: string;
  subUserID: string;
  productType: string;
  type: string;
  money: Record<string, string>;
  label: string;
};

const LIST_KEYS = ['list', 'result', 'records', 'rows', 'items', 'data'];

export const unwrapList = (res: any, depth = 0): any[] => {
  if (Array.isArray(res)) return res;
  if (!res || typeof res !== 'object' || depth > 3) return [];
  for (const key of LIST_KEYS) {
    if (res[key] == null) continue;
    const value = res[key];
    if (Array.isArray(value)) return value;
    const nested = unwrapList(value, depth + 1);
    if (nested.length) return nested;
  }
  return [];
};

export const accountNumberOf = (row: any) => String(row?.accountNum ?? row?.accountNumber ?? '').trim();

export const accountSubUserId = (row: any) => String(row?.subUserId ?? row?.subUserID ?? '').trim();

export const isSecuritiesAccount = (row: any) => String(row?.productType ?? '').trim() !== 'C';

/** 与 App 入口 mode 一致：虚拟只加密户，证券只证券户 */
export const filterAccountsForEntryMode = (accounts: ExchangeAccount[]): ExchangeAccount[] => {
  const entry = getDepositEntry();
  if (entry.mode === 'virtual') return accounts.filter((item) => item.productType === 'C');
  if (entry.mode === 'security') return accounts.filter((item) => item.productType !== 'C');
  return accounts;
};

export const overviewAccountsOf = (res: any) => {
  const list = unwrapList(res);
  const nested = list.some((item) => Array.isArray(item?.account) && !accountNumberOf(item));
  const flat = nested ? list.flatMap((item) => item.account || []) : list;
  return flat.filter((item) => accountNumberOf(item));
};

/** 证券户在前，加密户在后 */
export const sortAccounts = (list: any[]) => {
  const securities: any[] = [];
  const crypto: any[] = [];
  list.forEach((item) => {
    if (isSecuritiesAccount(item)) securities.push(item);
    else crypto.push(item);
  });
  return [...securities, ...crypto];
};

/** 优先接口 label。否则与入金、出金同一套：孖展 / 现金 / 虚拟，不用「股票账户 - 号 现金」 */
export const accountLabel = (row: any) => {
  const given = String(row?.label ?? '').trim();
  if (given) return given;
  const num = accountNumberOf(row);
  if (String(row?.productType ?? '').trim() === 'C') return `${i18n.FundH5.AccountVirtual}-${num}`;
  const type = String(row?.type ?? '').trim();
  if (type === 'M') return `${i18n.FundH5.AccountMargin}-${num}`;
  if (type === 'C') return `${i18n.FundH5.AccountCash}-${num}`;
  return `${i18n.FundH5.AccountStock}-${num}`;
};

export const moneyMapOf = (row: any): Record<string, string> => {
  const money: Record<string, string> = {};
  const rows = Array.isArray(row?.currencies)
    ? row.currencies
    : Array.isArray(row?.stockAccounts)
      ? row.stockAccounts
      : Array.isArray(row?.detail)
        ? row.detail
        : [];
  rows.forEach((item: any) => {
    const currency = String(item?.currency || '').trim();
    if (!currency) return;
    const bal = item?.withdrawalBal;
    if (bal == null || bal === '') return;
    money[currency] = String(bal);
  });
  return money;
};

export const toExchangeAccount = (row: any): ExchangeAccount => ({
  accountNum: accountNumberOf(row),
  subUserID: accountSubUserId(row),
  productType: String(row?.productType ?? '').trim(),
  type: String(row?.type ?? '').trim(),
  money: moneyMapOf(row),
  label: accountLabel(row),
});

const nonempty = (value: unknown) => String(value ?? '').trim();

const tradePermissionOf = (userInfo: any) => {
  const root = userInfo?.data ?? userInfo ?? {};
  const raw = root?.tradePermission;
  return raw && typeof raw === 'object' ? raw : {};
};

/**
 * URL accountNum（在列表里才用）>
 * 证券：modeType M → marginAccountNum，否则 cashAccountNum；
 * 虚拟：cryptoAccountNum；
 * 否则列表首项（证券在前）。
 */
export const pickAccountNum = (list: ExchangeAccount[], urlAccountNum?: string) => {
  const fromUrl = nonempty(urlAccountNum);
  if (fromUrl && list.some((item) => item.accountNum === fromUrl)) return fromUrl;

  const perm = tradePermissionOf(userState.userInfo);
  const entry = getDepositEntry();
  const preferred =
    entry.mode === 'virtual'
      ? nonempty(perm.cryptoAccountNum)
      : entry.modeType === 'M'
        ? nonempty(perm.marginAccountNum)
        : nonempty(perm.cashAccountNum);
  if (preferred && list.some((item) => item.accountNum === preferred)) return preferred;

  return list[0]?.accountNum || '';
};

/** 将单币种可兑金额写入对应账户 money */
export const applyCashBalanceByCurrency = (
  accounts: ExchangeAccount[],
  accountNum: string,
  currency: string,
  balance: number,
): ExchangeAccount[] => {
  const num = nonempty(accountNum);
  const cur = nonempty(currency);
  if (!num || !cur || !Number.isFinite(balance)) return accounts;
  return accounts.map((item) =>
    item.accountNum === num
      ? { ...item, money: { ...item.money, [cur]: String(balance) } }
      : item,
  );
};

/** App 预拉包是否够铺首屏（至少要有账户列表） */
export const isUsableExchangePrefetch = (ex: ExchangePrefetchPayload | null | undefined) => {
  if (!ex || typeof ex !== 'object') return false;
  return overviewAccountsOf(ex.stockAccountsMeta).length > 0;
};

export type ExchangeHydrateResult = {
  accounts: ExchangeAccount[];
  accountNum: string;
  currencies: string[];
  fromCurrency: string;
  toCurrency: string;
  rate: number;
  /** 有预拉汇率且与默认币对一致时，供页面跳过首屏 rates 请求 */
  seededRatePair: string;
  /** 有预拉可兑金额且与默认兑出币一致时，供页面跳过首屏余额请求 */
  seededBalanceKey: string;
  needCurrencies: boolean;
  needMoney: boolean;
};

/** 用 payload.exchange 拼出首屏状态；缺币种/余额时由页面补拉 */
export const hydrateFromExchangePrefetch = (
  ex: ExchangePrefetchPayload,
  urlAccountNum?: string,
): ExchangeHydrateResult => {
  let accounts = filterAccountsForEntryMode(
    sortAccounts(overviewAccountsOf(ex.stockAccountsMeta)).map(toExchangeAccount),
  );

  const fromExchange = nonempty(ex.accountNum);
  const accountNum =
    fromExchange && accounts.some((item) => item.accountNum === fromExchange)
      ? fromExchange
      : pickAccountNum(accounts, urlAccountNum);

  const currencies = unwrapCurrencyList(ex.availableCurrencies);
  const fromCurrency = currencies[0] || '';
  const toCurrency = currencies[1] || '';

  let seededBalanceKey = '';
  const bal = ex.cashBalanceByCurrency;
  if (bal && accountNum && fromCurrency) {
    const balAccount = nonempty(bal.accountNum);
    const balCurrency = nonempty(bal.currency);
    // data: null 时不灌数——Number(null)===0 会误显示可兑金额
    const rawBal = bal.balance;
    if (balAccount === accountNum && balCurrency === fromCurrency && rawBal != null && rawBal !== '') {
      const n = typeof rawBal === 'number' ? rawBal : Number(rawBal);
      if (Number.isFinite(n)) {
        accounts = applyCashBalanceByCurrency(accounts, accountNum, fromCurrency, n);
        seededBalanceKey = `${accountNum}|${fromCurrency}`;
      }
    }
  }

  let rate = 0;
  let seededRatePair = '';
  const rates = ex.rates;
  if (rates && fromCurrency && toCurrency) {
    const rateFrom = nonempty(rates.from);
    const rateTo = nonempty(rates.to);
    const n = Number(rates.rate);
    if (rateFrom === fromCurrency && rateTo === toCurrency && Number.isFinite(n) && n > 0) {
      rate = n;
      seededRatePair = `${fromCurrency}|${toCurrency}`;
    }
  }

  return {
    accounts,
    accountNum,
    currencies,
    fromCurrency,
    toCurrency,
    rate,
    seededRatePair,
    seededBalanceKey,
    needCurrencies: currencies.length === 0 && !!accountNum,
    needMoney: !seededBalanceKey && !!accountNum && !!fromCurrency,
  };
};

export const readExchangeQuery = () => {
  const q = (location.query || {}) as Record<string, string>;
  return { accountNum: nonempty(q.accountNum) };
};

/** 站内跳转带上首屏 platform / lang / theme / riseFallMode / safeTop / safeBottom / hideNav，业务参数自己传 */
export const withExchangeQuery = (path: string, extra: Record<string, string> = {}) => {
  const params = new URLSearchParams();
  Object.entries(extra).forEach(([key, value]) => {
    if (value) params.set(key, value);
  });
  appendDepositAppQuery(params);
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
};

/** 资金记录：带 mode / modeType；成功「查看记录」传 isBackToApp */
export const exchangeRecordsUrl = (accountNum: string, opts?: { isBackToApp?: boolean }) => {
  const entry = getDepositEntry();
  return withExchangeQuery('/h5/fund-records', {
    type: 'exchange',
    accountNum,
    ...(entry.mode ? { mode: entry.mode } : {}),
    ...(entry.modeType ? { modeType: entry.modeType } : {}),
    ...(opts?.isBackToApp ? { isBackToApp: '1' } : {}),
  });
};

export const exchangePageUrl = (accountNum?: string) =>
  withExchangeQuery('/h5/exchange', accountNum ? { accountNum } : {});

export const currencyName = (code: string) => {
  const upper = String(code || '').trim().toUpperCase();
  if (upper === 'HKD') return i18n.FundH5.CurrencyHkd;
  if (upper === 'USD') return i18n.FundH5.CurrencyUsd;
  if (upper === 'CNY') return i18n.FundH5.CurrencyCny;
  return code || '';
};

/** @deprecated 使用 CurrencyFlag 组件 */
export const currencyIcon = currencySpriteName;

export const unwrapCurrencyList = (res: any): string[] => {
  const list = unwrapList(res);
  if (list.length && typeof list[0] === 'string') return list.map(String);
  if (Array.isArray(res) && res.every((item) => typeof item === 'string')) return res as string[];
  return [];
};

export const parseRate = (res: any): number => {
  if (Array.isArray(res)) {
    const n = Number(res[0]?.rate);
    return Number.isFinite(n) ? n : 0;
  }
  if (res && typeof res === 'object') {
    const direct = Number(res.rate);
    if (Number.isFinite(direct) && direct > 0) return direct;
    const nested = res.data;
    if (Array.isArray(nested)) {
      const n = Number(nested[0]?.rate);
      return Number.isFinite(n) ? n : 0;
    }
    if (nested && typeof nested === 'object') {
      const n = Number(nested.rate);
      return Number.isFinite(n) ? n : 0;
    }
  }
  return 0;
};

/** 只留数字和一个小数点；小数最多 2 位 */
export const sanitizeAmount = (input: string) => {
  let text = String(input ?? '').replace(/[^\d.]/g, '');
  if (!text) return '';
  const dot = text.indexOf('.');
  let intPart = dot >= 0 ? text.slice(0, dot) : text;
  const decPart = dot >= 0 ? text.slice(dot + 1).replace(/\./g, '') : null;
  if (dot === 0) intPart = '0';
  intPart = intPart.replace(/^0+(?=\d)/, '');
  if (!intPart) intPart = '0';
  if (intPart.length > 15) intPart = intPart.slice(0, 15);
  if (decPart == null) return intPart;
  return `${intPart}.${decPart.slice(0, 2)}`;
};

const trimAmount = (text: string) => {
  if (!text.includes('.')) return text;
  return text.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
};

/** 向下截断到 2 位 */
export const truncMul = (amount: string, rate: number) => {
  const a = Number(amount);
  if (!amount || !Number.isFinite(a) || !Number.isFinite(rate) || rate <= 0) return '';
  const trunc = Math.floor(a * rate * 100 + 1e-10) / 100;
  return trimAmount(trunc.toFixed(2));
};

export const truncDiv = (amount: string, rate: number) => {
  const a = Number(amount);
  if (!amount || !Number.isFinite(a) || !Number.isFinite(rate) || rate <= 0) return '';
  const trunc = Math.floor((a / rate) * 100 + 1e-10) / 100;
  return trimAmount(trunc.toFixed(2));
};

export const parseBalance = (raw: string) => {
  if (!raw || raw === '-') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

export const is2goNotLoggedIn = (error: unknown) => {
  if (error == null) return false;
  if (typeof error === 'object') {
    const code = (error as { code?: unknown }).code;
    if (code != null && String(code) === '90001') return true;
  }
  return /^\s*90001\b/.test(String(error));
};
